// JSON-LD schema builders. Each returns a plain JS object; SEO.jsx serializes
// to JSON inside a <script type="application/ld+json">.
import { SITE_URL, SITE_NAME, LEGAL_NAME, DEFAULT_OG_IMAGE, CONTACT, SAME_AS } from './siteConfig';

// Service schema for the core 3PL offering — eligible for Service rich
// results and reinforces the entity around the LocalBusiness for queries
// like "3PL fulfillment", "ecommerce fulfillment Florida", etc.
export const serviceSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${SITE_URL}/#service`,
  name: 'Third-Party Logistics (3PL) Fulfillment',
  serviceType: '3PL Fulfillment',
  description:
    'Pick, pack, and ship fulfillment for Shopify, TikTok Shop, Amazon FBM, Etsy, and other DTC ecommerce brands. $2 per order, no minimums, no contracts, same-day shipping from South Florida.',
  url: SITE_URL,
  provider: { '@id': `${SITE_URL}/#organization` },
  areaServed: { '@type': 'Country', name: 'United States' },
  audience: {
    '@type': 'BusinessAudience',
    audienceType: 'Direct-to-Consumer brands, Shopify merchants, TikTok Shop sellers',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Fulfillment Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: 'Pick & Pack' },
        price: '2.00',
        priceCurrency: 'USD',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '2.00',
          priceCurrency: 'USD',
          unitText: 'order',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: 'Pallet Storage' },
        price: '25.00',
        priceCurrency: 'USD',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '25.00',
          priceCurrency: 'USD',
          unitText: 'pallet / month',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: 'Receiving & Inbound Handling' },
      },
      {
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: 'Returns Processing' },
      },
      {
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: 'Kitting & Custom Packaging' },
      },
    ],
  },
});

// Site-wide Organization schema — included on every page so Google can
// build a strong entity graph around the brand.
export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
  legalName: LEGAL_NAME,
  url: SITE_URL,
  logo: DEFAULT_OG_IMAGE,
  email: CONTACT.email,
  sameAs: SAME_AS,
  address: {
    '@type': 'PostalAddress',
    addressLocality: CONTACT.addressLocality,
    addressRegion: CONTACT.addressRegion,
    addressCountry: CONTACT.addressCountry,
  },
});

// LocalBusiness schema — used on Landing + About to anchor the brand to a
// physical service area (boosts local map and "[city] 3PL" queries).
export const localBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#localbusiness`,
  name: SITE_NAME,
  url: SITE_URL,
  image: DEFAULT_OG_IMAGE,
  email: CONTACT.email,
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    addressLocality: CONTACT.addressLocality,
    addressRegion: CONTACT.addressRegion,
    addressCountry: CONTACT.addressCountry,
  },
  areaServed: {
    '@type': 'Country',
    name: 'United States',
  },
  // Real public Trustpilot rating — must match what's visibly displayed on
  // the page (or trustpilot.com profile) so Google honors it for stars in
  // search results. Update these numbers when Trustpilot count changes.
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.3',
    bestRating: '5',
    worstRating: '1',
    reviewCount: '7',
  },
});

// FAQPage schema for /faq — eligible for FAQ rich results in Google search.
// Pass an array of { question, answer } where answer is plain text.
export const faqPageSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: f.answer,
    },
  })),
});

// Article schema for /blog/:slug — feeds Google Discover and article
// rich results.
export const articleSchema = ({ slug, title, excerpt, datePublished, category }) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: title,
  description: excerpt,
  datePublished,
  dateModified: datePublished,
  articleSection: category,
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${SITE_URL}/blog/${slug}`,
  },
  url: `${SITE_URL}/blog/${slug}`,
  image: DEFAULT_OG_IMAGE,
  author: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
  },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: DEFAULT_OG_IMAGE,
    },
  },
});

// BreadcrumbList for blog articles.
export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: item.name,
    item: item.url,
  })),
});

// WebSite schema with SearchAction — improves how Google displays sitelinks.
export const websiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  publisher: { '@id': `${SITE_URL}/#organization` },
});
